import { useCallback, useEffect, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, KpiCard } from "@/components/KpiCard";
import { StatusBadge } from "@/components/StatusBadge";
import { EditEntryDialog } from "@/components/EntryDialogs";
import { api } from "@/lib/api";
import { fmtNum, fmtEur, fmtDate, kmlTone, lastMonths } from "@/lib/format";

const MONTHS = lastMonths(12);

export default function OperatorHistory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [period, setPeriod] = useState(MONTHS[0].value);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/entries", { params: { month: period === "30d" ? undefined : period } })
      .then((r) => setRows(r.data)).finally(() => setLoading(false));
  }, [period]);
  useEffect(load, [load]);

  const km = rows.reduce((a, r) => a + (r.kilometers || 0), 0);
  const lt = rows.reduce((a, r) => a + (r.fuel_liters || 0), 0);
  const both = rows.filter((r) => r.kilometers && r.fuel_liters);
  const bothLt = both.reduce((a, r) => a + r.fuel_liters, 0);
  const cost = rows.reduce((a, r) => a + r.total_cost, 0);
  const flagged = rows.filter((r) => r.status === "Rejected");
  const vehicles = rows.reduce((a, r) => a + (r.vehicles_out || 0), 0);

  return (
    <div className="fade-up">
      <PageHeader title="Storico e riepilogo" subtitle="Gasolio inserito dalla tua stazione e stato di approvazione.">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-52" data-testid="history-period-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="30d">Ultimi 30 giorni</SelectItem>
            {MONTHS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </PageHeader>
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard label="Importo totale gasolio" value={fmtEur(cost)} sub={`${rows.length} giorni registrati`} testId="history-kpi-cost" />
        <KpiCard label="Litri calcolati" value={fmtNum(lt, 0)} testId="history-kpi-liters" />
        <KpiCard label="Media mezzi/giorno" value={fmtNum(rows.length ? vehicles / rows.length : 0, 1)} sub={`${fmtNum(km, 0)} km totali`} testId="history-kpi-vehicles" />
        <KpiCard label="Media km/l" value={fmtNum(bothLt ? both.reduce((a, r) => a + r.kilometers, 0) / bothLt : null, 2)} testId="history-kpi-kml" />
      </div>

      {flagged.length > 0 && (
        <div className="mb-6 rounded-xl border border-rose-200 bg-rose-50 p-4" data-testid="flagged-alert">
          <p className="flex items-center gap-2 text-sm font-semibold text-rose-700"><AlertTriangle className="h-4 w-4" /> {flagged.length} record segnalati dall'amministrazione</p>
          <p className="mt-1 text-xs text-rose-600">Verifica le note e correggi i dati: il record tornerà in attesa di approvazione.</p>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="history-table">
            <thead className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">Data</th><th className="px-4 py-3 text-right">Mezzi</th><th className="px-4 py-3 text-right">KM</th>
                <th className="px-4 py-3 text-right">Importo</th><th className="px-4 py-3 text-right">€/L</th><th className="px-4 py-3 text-right">Litri</th>
                <th className="px-4 py-3 text-right">km/l</th><th className="px-4 py-3">Stato</th><th className="px-4 py-3">Note amministrazione</th><th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y">
              {rows.map((r) => (
                <tr key={r.id} className={`hover:bg-slate-50 ${r.status === "Rejected" ? "bg-rose-50/40" : ""}`} data-testid={`history-row-${r.id}`}>
                  <td className="whitespace-nowrap px-4 py-3 font-medium">{fmtDate(r.date)}</td>
                  <td className="font-mono-num px-4 py-3 text-right" data-testid={`history-vehicles-${r.id}`}>{r.vehicles_out ?? "—"}</td>
                  <td className="font-mono-num px-4 py-3 text-right">{fmtNum(r.kilometers, 1)}</td>
                  <td className="font-mono-num px-4 py-3 text-right font-semibold" data-testid={`history-amount-${r.id}`}>{fmtEur(r.total_cost)}</td>
                  <td className="font-mono-num px-4 py-3 text-right">{fmtNum(r.fuel_price_per_liter, 3)}</td>
                  <td className="font-mono-num px-4 py-3 text-right text-slate-500">{fmtNum(r.fuel_liters, 2)}</td>
                  <td className="px-4 py-3 text-right"><span className={`font-mono-num rounded-md border px-2 py-0.5 text-xs font-semibold ${kmlTone(r.km_per_liter)}`}>{fmtNum(r.km_per_liter, 2)}</span></td>
                  <td className="px-4 py-3"><StatusBadge status={r.status} testId={`history-status-${r.id}`} /></td>
                  <td className="max-w-xs px-4 py-3 text-xs text-rose-700" data-testid={`history-note-${r.id}`}>{r.admin_notes || <span className="text-slate-400">—</span>}</td>
                  <td className="px-4 py-3 text-right">
                    {r.status !== "Approved" && (
                      <Button size="sm" variant="ghost" onClick={() => setEditing(r)} data-testid={`history-edit-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                    )}
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-12 text-center text-slate-500" data-testid="history-empty">Nessun record nel periodo selezionato.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <EditEntryDialog entry={editing} isAdmin={false} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </div>
  );
}
